import { Banknote, CreditCard, DollarSign, Check, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import { Checkbox } from '@/app/components/ui/checkbox'
import { CatalogSelect } from '@/app/components/ui/CatalogSelect'
import type { CashCapacity } from '@/app/services/pos.service'

export interface PaymentModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  total: number
  selectedPaymentMethod: string | null
  setSelectedPaymentMethod: (method: string | null) => void
  cashReceived: string
  setCashReceived: (value: string) => void
  change: number
  selectedBank: any
  setSelectedBank: (bank: any) => void
  reference: string
  setReference: (ref: string) => void
  requiresInvoice: boolean
  setRequiresInvoice: (requires: boolean) => void
  ticketCopies: number
  setTicketCopies: (copies: number) => void
  proceedToPayment: () => void
  isValidatingCart: boolean
  cashCapacity: CashCapacity | null
}

export function PaymentModal({
  open,
  onOpenChange,
  total,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  cashReceived,
  setCashReceived,
  change,
  selectedBank,
  setSelectedBank,
  reference,
  setReference,
  requiresInvoice,
  setRequiresInvoice,
  ticketCopies,
  setTicketCopies,
  proceedToPayment,
  isValidatingCart,
  cashCapacity,
}: PaymentModalProps) {
  const cashCapacityExceeded = Boolean(cashCapacity) && total > Number(cashCapacity?.remaining_amount || 0)
  const blocksCashContinue = cashCapacity?.status === 'CRITICAL' && cashCapacityExceeded

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Procesar Pago</DialogTitle>
          <DialogDescription className="sr-only">
            Seleccione el método de pago y complete la transacción
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-4">
            <div className="mb-1 flex justify-between">
              <span className="text-sm text-slate-600">Total a pagar:</span>
              <span className="text-2xl font-semibold text-slate-900">
                ${total.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Método de pago:</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setSelectedPaymentMethod('cash')}
                className={`rounded-lg border-2 p-4 transition-all ${selectedPaymentMethod === 'cash'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
                  }`}
              >
                <Banknote className="mx-auto mb-2 h-6 w-6" />
                <p className="text-xs font-medium">Efectivo</p>
              </button>
              <button
                onClick={() => setSelectedPaymentMethod('card')}
                className={`rounded-lg border-2 p-4 transition-all ${selectedPaymentMethod === 'card'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
                  }`}
              >
                <CreditCard className="mx-auto mb-2 h-6 w-6" />
                <p className="text-xs font-medium">Tarjeta de débito</p>
              </button>
              <button
                onClick={() => setSelectedPaymentMethod('transfer')}
                className={`rounded-lg border-2 p-4 transition-all ${selectedPaymentMethod === 'transfer'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
                  }`}
              >
                <DollarSign className="mx-auto mb-2 h-6 w-6" />
                <p className="text-xs font-medium">Transferencia</p>
              </button>
              <button
                onClick={() => setSelectedPaymentMethod('credit')}
                className={`rounded-lg border-2 p-4 transition-all ${selectedPaymentMethod === 'credit'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-slate-300'
                  }`}
              >
                <CreditCard className="mx-auto mb-2 h-6 w-6" />
                <p className="text-xs font-medium">Tarjeta de crédito</p>
              </button>
            </div>
          </div>

          {selectedPaymentMethod === 'cash' && (
            <div className="space-y-2">
              {cashCapacity?.status === 'CRITICAL' && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                  <p className="text-sm font-medium">
                    {cashCapacity.label}: {cashCapacity.message}
                  </p>
                </div>
              )}
              <label className="text-sm font-medium text-slate-700">
                Efectivo recibido:
              </label>
              <Input
                type="number"
                value={cashReceived}
                onChange={(e) => setCashReceived(e.target.value)}
                placeholder="0.00"
              />
              {parseFloat(cashReceived || '0') >= total && (
                <div className="rounded-lg bg-green-50 p-3">
                  <p className="text-sm text-green-700">
                    Cambio:{' '}
                    <span className="font-semibold">${change.toFixed(2)}</span>
                  </p>
                </div>
              )}
            </div>
          )}

          {(selectedPaymentMethod === 'card' || selectedPaymentMethod === 'credit') && (
            <div className="grid gap-3 sm:grid-cols-1">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Banco (opcional)
                </label>
                <CatalogSelect
                  catalog="banks"
                  placeholder="Ej. BBVA"
                  returnObject={true}
                  value={selectedBank?.id || ''}
                  preselectFirst={true}
                  onChange={(val: any) => {
                    setSelectedBank(val || null);
                  }}
                  itemLabelFormatter={(item: any) => item.name || item.title || item.description || `ID: ${item.id}`}
                />
              </div>
            </div>
          )}

          {selectedPaymentMethod === 'transfer' && (
            <div className="grid gap-3 sm:grid-cols-1">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Banco (opcional)
                </label>
                <CatalogSelect
                  catalog="banks"
                  placeholder="Ej. BBVA"
                  returnObject={true}
                  value={selectedBank?.id || ''}
                  preselectFirst={true}
                  onChange={(val: any) => {
                    setSelectedBank(val || null);
                  }}
                  itemLabelFormatter={(item: any) => item.name || item.title || item.description || `ID: ${item.id}`}
                />
              </div>
            </div>
          )}

          <div className="space-y-4 border-t border-slate-200 pt-4">
            <div
              className={`flex items-start space-x-3 rounded-lg border p-4 transition-colors cursor-pointer ${requiresInvoice ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                }`}
              onClick={() => setRequiresInvoice(!requiresInvoice)}
            >
              <Checkbox
                id="requires-invoice"
                checked={requiresInvoice}
                onCheckedChange={(checked) => setRequiresInvoice(checked as boolean)}
                className="mt-0.5"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="space-y-1">
                <label
                  htmlFor="requires-invoice"
                  className="text-sm font-medium text-slate-900 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  ¿Requiere factura?
                </label>
                <p className="text-xs text-slate-500">
                  Marque esta opción si el cliente solicita comprobante fiscal (CFDI).
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-900">
                  Tickets a imprimir
                </label>
                <p className="text-xs text-slate-500">
                  Cantidad de copias del recibo
                </p>
              </div>
              <div className="w-24">
                <Input
                  type="number"
                  min="1"
                  value={ticketCopies || ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setTicketCopies(isNaN(val) ? 0 : val);
                  }}
                  onBlur={() => {
                    if (!ticketCopies || ticketCopies < 1) setTicketCopies(1);
                  }}
                  className="text-center"
                />
              </div>
            </div>
          </div>

          <Button
            onClick={proceedToPayment}
            disabled={
              isValidatingCart ||
              !selectedPaymentMethod ||
              (selectedPaymentMethod === 'cash' && blocksCashContinue) ||
              (selectedPaymentMethod === 'cash' && parseFloat(cashReceived || '0') < total)
            }
            className="w-full"
            size="lg"
          >
            {isValidatingCart && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isValidatingCart ? 'Validando carrito...' : 'Continuar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export interface SuccessModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ticketNumber: string
  total: number
  requiresInvoice: boolean
  invoiceStatus: 'idle' | 'loading' | 'success' | 'error'
  invoiceError: string | null
  cfdiPdfBase64: string | null
  onDownloadPdf: () => void
  onDownloadXml: () => void
  onClose: () => void
}

export function SuccessModal({
  open,
  onOpenChange,
  ticketNumber,
  total,
  requiresInvoice,
  invoiceStatus,
  invoiceError,
  cfdiPdfBase64,
  onDownloadPdf,
  onDownloadXml,
  onClose,
}: SuccessModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-sm"
        aria-describedby="success-description"
      >
        <div className="py-6 text-center" id="success-description">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="mb-2 text-xl font-semibold text-slate-900">
            ¡Venta Completada!
          </h3>
          <p className="mt-4 text-2xl font-semibold text-slate-900">
            ${total.toFixed(2)}
          </p>
          {requiresInvoice && (
            <div className="mt-4 rounded-lg bg-slate-50 p-4 border border-slate-100">
              {invoiceStatus === 'loading' && (
                <p className="text-sm text-blue-600 flex items-center justify-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></span>
                  Generando factura...
                </p>
              )}
              {invoiceStatus === 'success' && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-green-600 flex items-center justify-center gap-1">
                    <Check className="h-4 w-4" /> Factura generada
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={onDownloadPdf}
                    disabled={!cfdiPdfBase64}
                  >
                    Descargar PDF
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={onDownloadXml}
                    disabled={!cfdiPdfBase64}
                  >
                    Descargar XML
                  </Button>
                </div>
              )}
              {invoiceStatus === 'error' && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-600">
                    ⚠️ Error al facturar
                  </p>
                  <p className="text-xs text-amber-700/80">
                    {invoiceError || 'Ocurrió un error.'}
                  </p>
                </div>
              )}
            </div>
          )}
          <div className="mt-6">
            <Button onClick={onClose} className="w-full">
              Nueva Venta
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
